// Formats raw YouTube Analytics dimension keys (dimensionType "age_gender" -> dimensionKey like
// "age25-34.female"; dimensionType "country" -> dimensionKey like "ID") into display-ready labels.

const AGE_BUCKET_LABELS: Record<string, string> = {
  "age13-17": "13-17",
  "age18-24": "18-24",
  "age25-34": "25-34",
  "age35-44": "35-44",
  "age45-54": "45-54",
  "age55-64": "55-64",
  "age65-": "65+",
};

// Youngest-to-oldest, for sorting age-bucket rows into a sensible order
// instead of whatever order the backend happens to return them in.
export const AGE_BUCKET_ORDER = Object.values(AGE_BUCKET_LABELS);

const GENDER_LABELS: Record<string, string> = {
  male: "Male",
  female: "Female",
  user_specified_other: "Other",
};

export type ParsedAgeGenderRow = {
  ageLabel: string;
  gender: "male" | "female" | "other" | null;
  genderLabel: string | null;
};

export const parseAgeGenderKey = (rawLabel: string): ParsedAgeGenderRow => {
  const key = String(rawLabel || "").trim();
  const [ageKeyRaw, genderKeyRaw] = key.split(".");
  const ageKey = (ageKeyRaw || "").toLowerCase();
  const genderKey = (genderKeyRaw || "").toLowerCase();
  const ageLabel = AGE_BUCKET_LABELS[ageKey] || key.replace(/^age/i, "").trim() || key;

  let gender: ParsedAgeGenderRow["gender"] = null;
  let genderLabel: string | null = null;
  if (genderKey === "male") {
    gender = "male";
    genderLabel = GENDER_LABELS.male;
  } else if (genderKey === "female") {
    gender = "female";
    genderLabel = GENDER_LABELS.female;
  } else if (genderKey) {
    gender = "other";
    genderLabel = GENDER_LABELS[genderKey] || "Other";
  }
  return { ageLabel, gender, genderLabel };
};

let regionDisplayNames: Intl.DisplayNames | null = null;
try {
  regionDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });
} catch {
  regionDisplayNames = null;
}

const isAlpha2 = (value: string) => /^[A-Z]{2}$/.test(value);

export const countryFlagEmoji = (code: string): string => {
  const cc = String(code || "").trim().toUpperCase();
  if (!isAlpha2(cc)) return "";
  return cc.replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
};

export const countryName = (code: string): string => {
  const cc = String(code || "").trim().toUpperCase();
  if (!isAlpha2(cc)) return code;
  try {
    return regionDisplayNames?.of(cc) || cc;
  } catch {
    return cc;
  }
};
